'use client';

import { createBrowserClient } from '@supabase/ssr';
import type { Database } from '@platform/supabase/types/database-types.ts';

let supabaseClient: ReturnType<typeof createBrowserClient<Database>> | null = null;

/**
 * Creates a singleton Supabase client for browser/client-side usage.
 * Returns the same client instance on subsequent calls to avoid creating
 * multiple GoTrueClient instances in the browser.
 *
 * Uses @supabase/ssr's browser client which properly syncs session state
 * with Next.js middleware via cookies.
 *
 * Use in:
 * - Client components
 * - React hooks
 * - Realtime subscriptions
 * - Client-side queries
 *
 * @example
 * ```typescript
 * const supabase = createBrowserSupabaseClient();
 * const { data, error } = await supabase.from('users').select();
 * ```
 */
export function createBrowserSupabaseClient() {

  if (supabaseClient) {
    return supabaseClient;
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_CLIENT_KEY;

  if (!url || !key) {
    throw new Error(
      'Supabase client configuration is missing. Check your environment setup.'
    );
  }

  supabaseClient = createBrowserClient<Database>(url, key);
  return supabaseClient;
}
